// Per-line Bills disclosure on the staff Order Detail page (PR #154b
// follow-up, 2026-06-01). Service lines can carry vendor bills directly
// — e.g. a Shipping line that costs us $250 in FedEx — so the line's
// real margin can be computed. Mutually exclusive with PO-attached
// bills at the data layer (bills.purchase_order_id XOR line_item_id).
//
// Reads: GET /orders/:orderId/lines/:lineItemId/bills
// Writes: POST /bills with line_item_id set + minimal totals seed.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";

interface BillRow {
  id: string;
  vendorId: string;
  vendorName: string;
  vendorBillNumber: string | null;
  billDate: string;
  dueDate: string | null;
  status: string;
  total: string;
  paidAt: string | null;
  createdAt: string | null;
}

interface Vendor {
  id: string;
  name: string;
}

interface Props {
  orderId: string;
  lineItemId: string;
  /** The parent order's detail query key. Invalidated on
   *  create/update so the per-line Bills count + line margin
   *  refresh together. */
  detailQueryKey: QueryKey;
  /** Reps can attach bills while statusStage allows line edits. */
  editable: boolean;
  colSpan: number;
}

export function LineBillsSection(props: Props) {
  return (
    <tr className="bg-cream-50/40">
      <td className="px-4 py-3" />
      <td className="px-4 py-3" colSpan={Math.max(1, props.colSpan - 1)}>
        <Inner {...props} />
      </td>
    </tr>
  );
}

function Inner(props: Props) {
  const { orderId, lineItemId, detailQueryKey, editable } = props;
  const queryClient = useQueryClient();
  const billsKey = ["orders", orderId, "lines", lineItemId, "bills"] as const;

  const { data, isLoading } = useQuery({
    queryKey: billsKey,
    queryFn: () =>
      api<{ rows: BillRow[] }>(
        `/orders/${orderId}/lines/${lineItemId}/bills`,
      ),
  });
  const bills = data?.rows ?? [];

  const [isAttaching, setIsAttaching] = useState(false);

  const attachMutation = useMutation({
    mutationFn: async (body: {
      vendor_id: string;
      vendor_bill_number?: string;
      bill_date: string;
      due_date?: string | null;
      total: string;
    }) =>
      api<{ bill: BillRow }>("/bills", {
        method: "POST",
        body: JSON.stringify({
          ...body,
          line_item_id: lineItemId,
          // Manual entry routes through pending_review so the AP grid
          // surfaces it. Skip the "submit_for_review" step — the rep
          // already entered the totals manually.
          initial_status: "pending_review",
          subtotal: body.total,
          total: body.total,
        }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billsKey });
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
      // Also refresh the Project Bills tab list.
      void queryClient.invalidateQueries({
        queryKey: ["project"],
      });
      setIsAttaching(false);
    },
  });

  const billsTotal = bills.reduce((sum, b) => sum + Number(b.total || 0), 0);

  return (
    <div className="rounded-md border border-brown-100 bg-white">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Vendor bills ({bills.length})
        </span>
        {bills.length > 0 && (
          <span className="text-xs text-brown-400">
            Total billed{" "}
            <span className="font-semibold text-brown-500 tabular-nums">
              {billsTotal.toLocaleString(undefined, {
                style: "currency",
                currency: "USD",
              })}
            </span>
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="px-3 pb-3 text-xs text-brown-300">Loading bills…</div>
      ) : bills.length === 0 ? (
        <div className="px-3 pb-3 text-xs text-brown-300">
          No vendor bills attached to this line yet.
        </div>
      ) : (
        <table className="w-full border-t border-brown-50 text-xs">
          <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
            <tr>
              <th className="px-3 py-2 font-semibold">Bill #</th>
              <th className="px-3 py-2 font-semibold">Vendor</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold">Bill date</th>
              <th className="px-3 py-2 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brown-50">
            {bills.map((b) => (
              <tr key={b.id}>
                <td className="px-3 py-1.5 text-brown-500">
                  {b.vendorBillNumber ?? "—"}
                </td>
                <td className="px-3 py-1.5 text-brown-500">{b.vendorName}</td>
                <td className="px-3 py-1.5">
                  <StatusPill tone={statusTone(b.status)}>
                    {b.status.replace(/_/g, " ")}
                  </StatusPill>
                </td>
                <td className="px-3 py-1.5 text-brown-400">{b.billDate}</td>
                <td className="px-3 py-1.5 text-right text-brown-500 tabular-nums">
                  {Number(b.total).toLocaleString(undefined, {
                    style: "currency",
                    currency: "USD",
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editable && (
        <div className="border-t border-brown-50 px-3 py-2">
          {!isAttaching ? (
            <button
              type="button"
              onClick={() => setIsAttaching(true)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brown-400 hover:text-brown-500"
            >
              <Plus className="h-3 w-3" /> Attach bill
            </button>
          ) : (
            <AttachBillForm
              busy={attachMutation.isPending}
              onCancel={() => setIsAttaching(false)}
              onSubmit={(body) => attachMutation.mutate(body)}
              errorMessage={extractError(attachMutation.error)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function AttachBillForm(props: {
  busy: boolean;
  onSubmit: (body: {
    vendor_id: string;
    vendor_bill_number?: string;
    bill_date: string;
    due_date?: string | null;
    total: string;
  }) => void;
  onCancel: () => void;
  errorMessage: string | null;
}) {
  const { busy, onSubmit, onCancel, errorMessage } = props;
  const vendorsQ = useQuery({
    queryKey: ["vendors", "all"],
    queryFn: () => api<{ rows: Vendor[] }>("/vendors"),
    staleTime: 5 * 60 * 1000,
  });
  const vendors = vendorsQ.data?.rows ?? [];
  const todayIso = new Date().toISOString().slice(0, 10);

  const [vendorId, setVendorId] = useState("");
  const [billNumber, setBillNumber] = useState("");
  const [billDate, setBillDate] = useState(todayIso);
  const [dueDate, setDueDate] = useState("");
  const [total, setTotal] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!vendorId) {
          setLocalError("Pick a vendor.");
          return;
        }
        // Required (Stephanie 2026-11-03).
        if (!billNumber.trim()) {
          setLocalError("Enter the supplier's bill number.");
          return;
        }
        const totalNum = Number(total);
        if (!Number.isFinite(totalNum) || totalNum <= 0) {
          setLocalError("Total must be a positive number.");
          return;
        }
        setLocalError(null);
        onSubmit({
          vendor_id: vendorId,
          vendor_bill_number: billNumber.trim(),
          bill_date: billDate,
          due_date: dueDate || null,
          total: totalNum.toFixed(2),
        });
      }}
      className="flex flex-wrap items-end gap-2"
    >
      <label className="flex flex-col gap-0.5">
        <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-brown-300">
          Vendor
        </span>
        <select
          autoFocus
          value={vendorId}
          onChange={(e) => setVendorId(e.target.value)}
          disabled={busy || vendorsQ.isLoading}
          className="rounded-md border border-brown-100 bg-white px-2 py-1 text-xs text-brown-500"
        >
          <option value="">
            {vendorsQ.isLoading ? "Loading…" : "Select a vendor…"}
          </option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-brown-300">
          Supplier&apos;s bill # (required)
        </span>
        <input
          value={billNumber}
          onChange={(e) => setBillNumber(e.target.value)}
          maxLength={120}
          required
          aria-required="true"
          placeholder="From the bill"
          className="w-32 rounded-md border border-brown-100 bg-white px-2 py-1 text-xs text-brown-500"
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-brown-300">
          Bill date
        </span>
        <input
          type="date"
          value={billDate}
          onChange={(e) => setBillDate(e.target.value)}
          className="rounded-md border border-brown-100 bg-white px-2 py-1 text-xs text-brown-500"
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-brown-300">
          Due date <span className="font-normal text-brown-300">(optional)</span>
        </span>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="rounded-md border border-brown-100 bg-white px-2 py-1 text-xs text-brown-500"
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-brown-300">
          Total
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={total}
          onChange={(e) => setTotal(e.target.value)}
          placeholder="0.00"
          className="w-24 rounded-md border border-brown-100 bg-white px-2 py-1 text-right text-xs tabular-nums text-brown-500"
        />
      </label>
      <div className="flex items-center gap-2 pb-0.5">
        <Button type="submit" size="sm" disabled={busy}>
          {busy ? "Attaching…" : "Attach"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {(localError || errorMessage) && (
        <div className="basis-full text-xs text-rust-500">
          {localError ?? errorMessage}
        </div>
      )}
    </form>
  );
}

function statusTone(
  s: string,
): "active" | "prospect" | "dormant" | "closed" | "warn" | "info" {
  switch (s) {
    case "draft":
    case "pending_review":
      return "dormant";
    case "approved":
      return "prospect";
    case "paid":
      return "active";
    case "disputed":
      return "warn";
    case "voided":
      return "closed";
    default:
      return "info";
  }
}

function extractError(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as { error?: string };
    return body.error ?? `Couldn't attach the bill (${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return "Couldn't attach the bill";
}
